// src/components/admin/FAQsTab.js
import { useState, useEffect } from 'react';
import { Paper, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Radio, CircularProgress, Alert, Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import AddFaqModal from './AddFaqModal';
import ModifyFaqModal from './ModifyFaqModal';
import { getFaqs, deleteFaq, createFaq, updateFaq } from '../../services/adminApiClient';

export default function FAQsTab({ customers, loading: customersLoading, error: customersError }) {
    const [countryCode, setCountryCode] = useState('US');
    const [allFaqs, setAllFaqs] = useState({});
    const [displayedData, setDisplayedData] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [isAddModalOpen, setAddModalOpen] = useState(false);
    const [isModifyModalOpen, setModifyModalOpen] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState('');
    const [actionStatus, setActionStatus] = useState({ type: '', text: '' });

    const fetchData = async () => {
        setLoadingData(true);
        setError('');
        try {
            const response = await getFaqs();
            if (response.success && Array.isArray(response.data)) {
                const groupedData = response.data.reduce((acc, item) => {
                    const key = item.client_code;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push({
                        id: item.faq_id,
                        clientCode: item.client_code,
                        clientName: item.client_name,
                        content: item.content,
                        timestamp: item.created_at,
                        createdBy: item.last_updated_by || 'N/A'
                    });
                    return acc;
                }, {});
                setAllFaqs(groupedData);
            } else {
                throw new Error("Invalid data format received from API.");
            }
        } catch (e) {
            setError(e.message || "Failed to fetch FAQs.");
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    useEffect(() => {
        if (selectedCustomer && selectedCustomer.ClientCode) {
            setDisplayedData(allFaqs[selectedCustomer.ClientCode] || []);
            setSelectedRowId(null);
        } else {
            setDisplayedData([]);
        }
    }, [selectedCustomer, allFaqs]);

    const handleCustomerChange = (event) => {
        const cust = filteredCustomers.find(c => c.ClientCode === event.target.value);
        setSelectedCustomer(cust);
    };

    const handleCountryChange = (event) => {
        const newCountry = event.target.value;
        setCountryCode(newCountry);
        setSelectedCustomer(null);
    };

    const handleDelete = async () => {
        if (!selectedRowId || !window.confirm(`Are you sure you want to delete this FAQ?`)) return;
        try {
            await deleteFaq({ faq_id: selectedRowId });
            setActionStatus({ type: 'success', text: 'FAQ deleted successfully!' });
            fetchData();
        } catch (e) {
            setActionStatus({ type: 'error', text: e.message || 'Failed to delete FAQ.' });
        }
    };

    const handleAddSubmit = async (payload) => {
        try {
            await createFaq(payload);
            setAddModalOpen(false);
            fetchData();
            setActionStatus({ type: 'success', text: 'FAQ added successfully!' });
        } catch (e) {
            throw e;
        }
    };

    const handleModifySubmit = async (payload) => {
        try {
            await updateFaq(payload);
            setModifyModalOpen(false);
            fetchData();
            setActionStatus({ type: 'success', text: 'FAQ updated successfully!' });
        } catch (e) {
            throw e;
        }
    };

    const filteredCustomers = customers.filter(c => c.region === countryCode);

    if (customersLoading) return <CircularProgress />;
    if (customersError) return <Alert severity="error">Error loading customers: {customersError}</Alert>;

    return (
        <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>FAQs</Typography>
            {actionStatus.text && <Alert severity={actionStatus.type} sx={{ mb: 2 }}>{actionStatus.text}</Alert>}
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>COUNTRY</InputLabel>
                        <Select value={countryCode} label="COUNTRY" onChange={handleCountryChange}>
                            <MenuItem value="US">US</MenuItem>
                            <MenuItem value="CA">CA</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth>
                        <InputLabel>CUSTOMERS</InputLabel>
                        <Select
                            value={selectedCustomer ? selectedCustomer.ClientCode : ''}
                            label="CUSTOMERS"
                            onChange={handleCustomerChange}
                        >
                            {filteredCustomers.map(customer => (
                                <MenuItem key={customer.ClientCode} value={customer.ClientCode}>
                                    {customer.ClientName}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button variant="outlined" onClick={() => setAddModalOpen(true)} disabled={!selectedCustomer}>Add New</Button>
                    <Button variant="outlined" onClick={() => setModifyModalOpen(true)} disabled={!selectedRowId}>Modify</Button>
                    <Button variant="contained" color="error" onClick={handleDelete} disabled={!selectedRowId}>Delete</Button>
                </Grid>
            </Grid>

            {loadingData && <CircularProgress sx={{ mt: 2 }} />}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {!loadingData && !error && (
                <TableContainer component={Paper} sx={{ mt: 3, border: '1px solid #ddd' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 'bold' } }}>
                                <TableCell padding="checkbox"></TableCell>
                                <TableCell>FAQ ID</TableCell>
                                <TableCell>Content</TableCell>
                                <TableCell>Timestamp</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedData.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell padding="checkbox">
                                        <Radio checked={selectedRowId === row.id} onChange={() => setSelectedRowId(row.id)} value={row.id} />
                                    </TableCell>
                                    <TableCell sx={{ wordBreak: 'break-all' }}>{row.id}</TableCell>
                                    <TableCell>{row.content}</TableCell>
                                    <TableCell>{new Date(row.timestamp).toLocaleString()}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <AddFaqModal
                open={isAddModalOpen}
                onClose={() => setAddModalOpen(false)}
                customer={selectedCustomer}
                onSubmit={handleAddSubmit}
            />
            <ModifyFaqModal
                open={isModifyModalOpen}
                onClose={() => setModifyModalOpen(false)}
                customer={selectedCustomer}
                faq={displayedData.find(d => d.id === selectedRowId)}
                onSubmit={handleModifySubmit}
            />
        </Paper>
    );
}
